import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Gift, Copy, Check, Share2 } from 'lucide-react';
import { FaWhatsapp, FaFacebookF, FaTwitter } from 'react-icons/fa';

const ReferEarnWidget = () => {
  const [copied, setCopied] = useState(false);
  const referralCode = 'CLEANSE200';
  const referralLink = `https://cleansingtherapy.app/ref/${referralCode}`;
  const shareMessage = `🌿 I'm trying Cleansing Therapy for natural detox! Join me and get ₹200 off your first session. Use code ${referralCode} 🎁\n\n${referralLink}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Get ₹200 off Cleansing Therapy',
          text: shareMessage,
          url: referralLink,
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    }
  };

  const handleWhatsAppShare = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(shareMessage)}`,
      '_blank'
    );
  };

  const handleFacebookShare = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
      '_blank'
    );
  };

  const handleTwitterShare = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`,
      '_blank'
    );
  };

  return (
    <div id="refer-earn" className="py-20 px-4 bg-gradient-to-br from-primary/5 via-purple-50 to-pink-50">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-pink-500 text-white px-6 py-2 rounded-full mb-6">
            <Gift className="w-5 h-5" />
            <span className="font-semibold">Refer & Earn</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Share the Wellness ✨
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Give ₹200, Get ₹200. Help your friends discover natural detox and earn rewards together
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl"
        >
          {/* Reward Display */}
          <div className="grid md:grid-cols-2 gap-8 mb-10">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-br from-primary to-purple-600 rounded-2xl p-6 text-white"
            >
              <div className="text-5xl mb-3">🎁</div>
              <div className="text-3xl font-bold mb-2">₹200 OFF</div>
              <p className="text-purple-100">For your friend's first session</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl p-6 text-white"
            >
              <div className="text-5xl mb-3">💰</div>
              <div className="text-3xl font-bold mb-2">₹200 CASH</div>
              <p className="text-pink-100">In your wallet when they book</p>
            </motion.div>
          </div>

          {/* Referral Code */}
          <div className="mb-8">
            <label className="block text-gray-700 font-semibold mb-3 text-center">
              Your Referral Code
            </label>
            <div className="flex items-center gap-3 max-w-md mx-auto">
              <div className="flex-1 bg-gradient-to-r from-primary/10 to-purple-100 rounded-xl px-6 py-4 text-center">
                <span className="text-2xl font-bold text-primary tracking-wider">
                  {referralCode}
                </span>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCopy}
                className="w-14 h-14 bg-primary hover:bg-purple-700 text-white rounded-xl flex items-center justify-center transition-colors"
              >
                {copied ? <Check className="w-6 h-6" /> : <Copy className="w-6 h-6" />}
              </motion.button>
            </div>
            {copied && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center text-green-600 font-semibold mt-3"
              >
                ✓ Link copied to clipboard!
              </motion.p>
            )}
          </div>

          {/* Share Buttons */}
          <div className="space-y-4">
            <p className="text-center text-gray-600 font-semibold mb-4">
              Share via
            </p>

            {/* Web Share API (Mobile) */}
            {navigator.share && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleShare}
                className="w-full bg-gradient-to-r from-primary to-purple-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transition-all"
              >
                <Share2 className="w-5 h-5" />
                Share Now
              </motion.button>
            )}

            {/* Social Share Buttons */}
            <div className="grid grid-cols-3 gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleWhatsAppShare}
                className="bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl flex flex-col items-center justify-center gap-2 transition-colors"
              >
                <FaWhatsapp className="w-6 h-6" />
                <span className="text-sm font-semibold">WhatsApp</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleFacebookShare}
                className="bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl flex flex-col items-center justify-center gap-2 transition-colors"
              >
                <FaFacebookF className="w-6 h-6" />
                <span className="text-sm font-semibold">Facebook</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleTwitterShare}
                className="bg-sky-500 hover:bg-sky-600 text-white py-4 rounded-xl flex flex-col items-center justify-center gap-2 transition-colors"
              >
                <FaTwitter className="w-6 h-6" />
                <span className="text-sm font-semibold">Twitter</span>
              </motion.button>
            </div>
          </div>

          {/* How it Works */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <h3 className="text-center font-bold text-gray-800 mb-6">
              How It Works
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">
                  1
                </div>
                <p className="text-gray-600">
                  Share your referral code with friends
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">
                  2
                </div>
                <p className="text-gray-600">
                  They book using your code and save ₹200
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">
                  3
                </div>
                <p className="text-gray-600">
                  You get ₹200 cashback instantly
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ReferEarnWidget;
